/**
 * Pure date bucketing for the orders calendar. No React, no queries: the
 * calendar renders whatever rows the page's OWN list query already returned
 * (same filters, same RLS), and this module only decides which day cell a
 * row belongs to.
 *
 * Placement key: expected_delivery_date (when the pair arrives), never the
 * order date. Rows with NO expected date, and rows whose date is outside
 * the plausible window, go to the labelled no-date bucket instead of
 * vanishing or breaking the grid. BUCKETED, not clamped, by decision:
 * clamping an out-of-range date (the year-0020 class of bug) would place
 * the order on a fabricated day and misinform the booking call.
 */
import type { OrderListRow } from '@insolech/sdk';

export interface CalendarMonth {
  year: number;
  /** 1-12 */
  month: number;
}

/** Plausible expected-delivery window. Anything outside is treated as an
 *  invalid date and bucketed with the dateless orders (labelled). */
export const DELIVERY_DATE_MIN_YEAR = 2020;
export const DELIVERY_DATE_MAX_YEAR = 2032;

export function monthOf(d: Date): CalendarMonth {
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

/** Exactly one month per step, across year boundaries. */
export function addMonths(m: CalendarMonth, delta: number): CalendarMonth {
  const zero = m.year * 12 + (m.month - 1) + delta;
  return { year: Math.floor(zero / 12), month: (((zero % 12) + 12) % 12) + 1 };
}

export function sameMonth(a: CalendarMonth, b: CalendarMonth): boolean {
  return a.year === b.year && a.month === b.month;
}

export function monthLabel(m: CalendarMonth): string {
  const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${names[m.month - 1]} ${m.year}`;
}

/** yyyy-mm-dd for a day inside a month, no Date/timezone involved. */
const dayIso = (m: CalendarMonth, day: number): string =>
  `${String(m.year).padStart(4, '0')}-${String(m.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const daysInMonth = (m: CalendarMonth): number => new Date(m.year, m.month, 0).getDate();

export interface CalendarCell {
  iso: string;
  day: number;
  inMonth: boolean;
}

/** Monday-first 6x7-capped grid (weeks trimmed to those touching the month). */
export function monthGrid(m: CalendarMonth): CalendarCell[][] {
  const first = new Date(m.year, m.month - 1, 1);
  const lead = (first.getDay() + 6) % 7; // Monday = 0
  const total = daysInMonth(m);
  const cells: CalendarCell[] = [];
  const prev = addMonths(m, -1);
  const prevDays = daysInMonth(prev);
  for (let i = lead - 1; i >= 0; i--) {
    cells.push({ iso: dayIso(prev, prevDays - i), day: prevDays - i, inMonth: false });
  }
  for (let d = 1; d <= total; d++) cells.push({ iso: dayIso(m, d), day: d, inMonth: true });
  const next = addMonths(m, 1);
  let nd = 1;
  while (cells.length % 7 !== 0) cells.push({ iso: dayIso(next, nd), day: nd++, inMonth: false });
  const weeks: CalendarCell[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** The first 10 chars of expected_delivery_date when it is a plausible
 *  calendar day; null when missing or out of range. */
export function deliveryDayOf(row: Pick<OrderListRow, 'expected_delivery_date'>): string | null {
  const raw = row.expected_delivery_date;
  if (!raw) return null;
  const iso = String(raw).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const year = Number(m[1]);
  if (year < DELIVERY_DATE_MIN_YEAR || year > DELIVERY_DATE_MAX_YEAR) return null;
  return iso;
}

export interface BucketedOrders {
  /** yyyy-mm-dd -> rows expected that day (any month; the grid picks). */
  byDay: Map<string, OrderListRow[]>;
  /** No expected date, or a date outside the plausible window. */
  noDate: OrderListRow[];
}

export function bucketOrdersByDeliveryDay(rows: readonly OrderListRow[]): BucketedOrders {
  const byDay = new Map<string, OrderListRow[]>();
  const noDate: OrderListRow[] = [];
  for (const row of rows) {
    const day = deliveryDayOf(row);
    if (!day) {
      noDate.push(row);
      continue;
    }
    const list = byDay.get(day);
    if (list) list.push(row);
    else byDay.set(day, [row]);
  }
  return { byDay, noDate };
}
